import { Flex, Box, Text } from "@chakra-ui/react";
import { LOAN_TYPE_COLORS } from "components/analytics/LoanTypeBreakdown";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { LoanStatusPieLegend } from "./loan-status-pie-legend";

import { LoanStatusPieItem } from "./types/loan-status-pie-item";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { LoansContext } from "providers/loans";
import { shortLoanAmount } from "utils/helpers";
import WidgetBox from "components/elements/WidgetBox/widget-box";

export enum LoanStatusPieType {
  COMPLETED = "completed",
  UNCOMPLETED = "uncompleted",
}

interface LoanStatusPieProps {
  type: LoanStatusPieType;
}

export function LoanStatusPie({ type }: LoanStatusPieProps) {
  const { user } = useTenant();
  const { loans } = useContext(LoansContext);

  const [statuses, setStatuses] = useState<LoanStatusPieItem[]>([]);

  const hasDataInStatuses = statuses.some((status: LoanStatusPieItem) => status.value > 0);

  const custom_currency = useMemo(() => {
    return user?.company?.company_configurations?.[0]?.custom_currency?.currency || "$";
  }, [user]);

  useEffect(() => {
    const statuses: LoanStatusPieItem[] = user.company.statuses
      .filter((status: any) => !status.label.includes("Declined"))
      .filter((status: any) =>
        type === LoanStatusPieType.COMPLETED
          ? status.label.includes("Completed", "Redeemed")
          : !status.label.includes("Completed", "Redeemed")
      )
      .map((status: any) => {
        const totalOfLoans = loans
          .filter((loan: any) => loan.status.id === status.id)
          .reduce((acc: number, loan: any) => acc + Number(loan.amount), 0);

        return {
          name: status.label,
          formatedValue: shortLoanAmount(totalOfLoans, custom_currency),
          value: totalOfLoans,
        };
      });

    setStatuses(statuses);
  }, [user, loans, type, custom_currency]);

  return (
    <WidgetBox
      title={type === LoanStatusPieType.COMPLETED ? "Post-funded Loans" : "Pre-funded Loans"}
      borderRadius={"15px"}
    >
      {hasDataInStatuses ? (
        <Flex direction="row" gap={4} height={"200px"}>
          <Box flex={1}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={statuses}
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="80%"
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statuses.map((_entry, index) => (
                    <Cell
                      key={_entry.name}
                      fill={LOAN_TYPE_COLORS[index % LOAN_TYPE_COLORS.length]}
                    />
                  ))}
                </Pie>

                <Tooltip formatter={(value: any) => shortLoanAmount(value, custom_currency)} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box flex={1} alignContent={"center"}>
            <LoanStatusPieLegend data={statuses} />
          </Box>
        </Flex>
      ) : (
        <Text>No data available</Text>
      )}
    </WidgetBox>
  );
}
